import React, { useState, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { register } from "../functions/functions";
import { AnalyticsHandle } from "./AnalyticsHandle";

/* ───────────────────────── BRAND ───────────────────────── */
const C = {
  graphite: "#27272B",
  blue: "#3a6dff",
  blueHover: "#2d6ff4",
  blueLight: "#eef2ff",
  bg: "#f7f7f5",
  card: "#ffffff",
  text: "#1a1e21",
  text2: "#565e64",
  text3: "#969696",
  border: "#e5e5e0",
  green: "#198754",
  greenLight: "#eaf5ee",
  red: "#dc3545",
  redSoft: "#ff6b6b",
};

const fmt = new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 0 });
const zl = (n) => fmt.format(Math.round(n)) + " zł";

const DNI_ROBOCZE = 21;
const TYG_W_MIES = 4.33;

const DEFAULT_ASSUMPTIONS = {
  absencjeMin: {
    label: "Rozbieżności między czasem opłaconym a przepracowanym",
    unit: "min/mies./prac.", freq: "mies", degresja: true,
    value: 40, min: 0, max: 300, step: 5,
    desc: 'Spóźnienia, wcześniejsze wyjścia, "kryjące się" nieobecności. Czas opłacony, a nieprzepracowany. Wartość uśredniona w skali całej załogi. W większych zespołach przyjmujemy niższą wartość na osobę, bo procesy są lepiej poukładane.',
  },
  zaokraglenia: {
    label: "Zaokrąglenia godzin w górę",
    unit: "min/dzień/prac.", freq: "dzien", degresja: true,
    value: 4, min: 0, max: 20, step: 1,
    desc: "Papierowa lista zachęca do zaokrągleń. Drobne minuty, które w skali roku robią się kwotą.",
  },
  zbieranieMin: {
    label: "Zbieranie i przesyłanie papierowych list",
    unit: "min/tydz./prac.", freq: "tydzien", rate: "worker",
    value: 12, min: 0, max: 90, step: 1,
    desc: "Pracownicy lub brygadziści zbierają i przesyłają listy godzin do biura. Często co tydzień, czasem częściej.",
  },
  przepisywanieMin: {
    label: "Przepisywanie godzin i naliczanie wypłat",
    unit: "min/mies./prac.", freq: "mies", rate: "office",
    value: 16, min: 0, max: 60, step: 1,
    desc: "Ktoś w biurze przepisuje godziny z list do Excela lub systemu płacowego i nalicza wypłaty.",
  },
  sporyMin: {
    label: "Wyjaśnienia i spory przy wypłacie",
    unit: "min/mies./prac.", freq: "mies", rate: "mixed",
    value: 4, min: 0, max: 60, step: 1,
    desc: 'Rozbieżności "ja pracowałem dłużej", dyskusje, sprawdzanie kto ma rację. Czas biura i czas pracownika.',
  },
  urlopyMin: {
    label: "Sprawdzanie salda urlopów",
    unit: "min/kwartał/prac.", freq: "kwartal", rate: "office",
    value: 14, min: 0, max: 90, step: 1,
    desc: '"Ile mi zostało urlopu?". Ktoś w biurze co jakiś czas ręcznie to sprawdza i podlicza.',
  },
  rentownoscMin: {
    label: "Rozliczenie kosztu na inwestycję",
    unit: "min/mies./inwestycja", freq: "mies_inw", rate: "office",
    value: 22, min: 0, max: 180, step: 1,
    desc: "Przydzielanie godzin z papierowych list do konkretnych inwestycji i szacowanie ile dana inwestycja kosztowała.",
  },
};

function Slider({ label, value, onChange, min, max, step = 1, suffix = "", accent = C.blue }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
        <label style={{ fontSize: 14, color: C.text2 }}>{label}</label>
        <span style={{ fontSize: 15, fontWeight: 600, color: C.text, whiteSpace: "nowrap", flexShrink: 0 }}>{value}{suffix}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: accent, cursor: "pointer" }}
      />
    </div>
  );
}

function AssumptionRow({ a, onChange }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 600 });
  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, padding: "12px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <button
            onClick={() => setOpen(!open)}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
              width: "100%",
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: 14, color: C.text, fontWeight: 500, textAlign: "left",
              display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit",
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              {a.label}
            </span>
            <svg
              width="13" height="13" viewBox="0 0 16 16" fill="none"
              style={{
                flexShrink: 0,
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            >
              <path d="M4 6l4 4 4-4" stroke={hover || open ? C.blueHover : C.blue} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-end" : "center",
          gap: isMobile ? 4 : 12,
          flexShrink: 0,
          width: isMobile ? 90 : "auto",
        }}>
          <input
            type="number" value={a.value} min={a.min} max={a.max} step={a.step}
            onChange={(e) => onChange(Number(e.target.value))}
            onFocus={(e) => {
              const t = e.target;
              setTimeout(() => t.select(), 0);
            }}
            style={{
              width: 64, padding: "6px 8px", borderRadius: 8, border: `1px solid ${C.border}`,
              fontSize: 14, textAlign: "right", color: C.text, fontFamily: "inherit",
            }}
          />
          <span style={{
            fontSize: 12, color: C.text3,
            width: isMobile ? "auto" : 110,
            textAlign: isMobile ? "right" : "left",
          }}>{a.unit}</span>
        </div>
      </div>
      {open && (
        <p style={{ fontSize: 12.5, color: C.text2, margin: "8px 0 0", lineHeight: 1.5, maxWidth: 540 }}>
          {a.desc}
        </p>
      )}
    </div>
  );
}

function ResultBlock({ title, total, items, accent }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: C.text }}>{title}</h3>
        <span style={{ fontSize: 20, fontWeight: 700, color: accent, whiteSpace: "nowrap", flexShrink: 0 }}>{zl(total)}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, color: C.text2 }}>
            <span>{it.label}</span>
            <span style={{ fontWeight: 500, color: C.text, whiteSpace: "nowrap", flexShrink: 0 }}>{zl(it.value)}/rok</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function KalkulatorROI() {
  const [employees, setEmployees] = useState(15);
  const [rate, setRate] = useState(35);
  const [sites, setSites] = useState(4);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const { logRegisterClick } = AnalyticsHandle();

  // null = użytkownik nie ruszał, stosujemy wartość auto (z degresją dla wybranych pozycji).
  // liczba = użytkownik ręcznie nadpisał, szanujemy jego wartość.
  const [overrides, setOverrides] = useState(() =>
    Object.fromEntries(Object.keys(DEFAULT_ASSUMPTIONS).map((k) => [k, null]))
  );

  // Współczynnik degresji: w większych zespołach mniej minut "przecieka" na osobę,
  // bo procesy są lepiej poukładane. Liniowo od 1.0 (5 osób) do ~0.47 (50 osób).
  const e = Math.max(5, Math.min(50, employees));
  const degresja = 1 - 0.53 * ((e - 5) / 45);

  // Efektywna wartość pozycji: ręczne nadpisanie wygrywa, inaczej baza (z degresją jeśli dotyczy).
  const effective = (key) => {
    if (overrides[key] !== null) return overrides[key];
    const def = DEFAULT_ASSUMPTIONS[key];
    const base = def.value;
    return def.degresja ? Math.round(base * degresja) : base;
  };

  const setAssumption = (key, val) => setOverrides((prev) => ({ ...prev, [key]: val }));

  const A = Object.fromEntries(Object.keys(DEFAULT_ASSUMPTIONS).map((k) => [k, effective(k)]));

  const officeRate = rate * 1.6;

  const calc = useMemo(() => {
    const absencje = employees * (A.absencjeMin / 60) * rate * 12;
    const zaokraglenia = employees * (A.zaokraglenia / 60) * DNI_ROBOCZE * rate * 12;
    const osA = absencje + zaokraglenia;

    const zbieranie = employees * (A.zbieranieMin / 60) * TYG_W_MIES * rate * 12;
    const przepisywanie = employees * (A.przepisywanieMin / 60) * officeRate * 12;
    const spory = employees * (A.sporyMin / 60) * ((officeRate + rate) / 2) * 12;
    const urlopy = employees * (A.urlopyMin / 60) * officeRate * 4;
    const rentownosc = sites * (A.rentownoscMin / 60) * officeRate * 12;
    const osB = zbieranie + przepisywanie + spory + urlopy + rentownosc;

    const total = osA + osB;

    return {
      total,
      osA: { total: osA, absencje, zaokraglenia },
      osB: { total: osB, zbieranie, przepisywanie, spory, urlopy, rentownosc },
    };
  }, [employees, rate, sites, overrides, degresja, officeRate]);

  const btn = (primary) => ({
    padding: "13px 26px", borderRadius: 8, textDecoration: "none", fontSize: 15,
    fontWeight: 500, display: "inline-block", cursor: "pointer",
    fontFamily: "inherit",
    ...(primary
      ? { background: C.blue, color: "#fff", border: "none" }
      : { background: "transparent", color: C.graphite, border: `1px solid ${C.border}` }),
  });

  return (
    <div style={{
      maxWidth: 760, margin: "0 auto", padding: "36px 24px",
      fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      color: C.text, boxSizing: "border-box",
    }}>

      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 8px", color: C.graphite }}>
          Ile traci Twoja firma na papierowej ewidencji?
        </h2>
        <p style={{ fontSize: 15, color: C.text2, margin: 0, maxWidth: 520, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
          Ustaw trzy parametry dla swojej firmy. Resztę policzymy na podstawie tego, co
          widzimy u naszych klientów.
        </p>
      </div>

      <div style={{
        background: C.graphite, borderRadius: 16, padding: "28px 24px",
        textAlign: "center", marginBottom: 24, position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3, background: C.red,
        }} />
        <p style={{ fontSize: 12.5, color: C.redSoft, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
          Szacowana roczna strata
        </p>
        <p style={{ fontSize: 46, fontWeight: 800, margin: 0, lineHeight: 1.05, color: C.redSoft }}>
          {zl(calc.total)}
        </p>
        <p style={{ fontSize: 14, color: "#b8bcc4", margin: "8px 0 0" }}>
          ≈ {zl(calc.total / 12)} miesięcznie. Tyle eGodziny pomagają odzyskać.
        </p>
      </div>

      <div style={{ background: C.bg, borderRadius: 16, padding: "22px 22px 6px", marginBottom: 16 }}>
        <Slider label="Liczba pracowników" value={employees} onChange={setEmployees} min={3} max={100} />
        <Slider label="Średnia stawka pracownika (PLN/h brutto)" value={rate} onChange={setRate} min={25} max={120} suffix=" zł" />
        <Slider label="Ile inwestycji prowadzisz miesięcznie?" value={sites} onChange={setSites} min={1} max={30} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => setShowAssumptions(!showAssumptions)}
          style={{
            width: "100%", background: C.card, border: `1px solid ${C.border}`,
            borderRadius: showAssumptions ? "12px 12px 0 0" : 12,
            cursor: "pointer", padding: "14px 18px",
            fontSize: 14, color: C.text, fontWeight: 500, fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            transition: "border-radius 0.15s",
          }}
        >
          <span>Jak to liczymy?</span>
          <svg
            width="16" height="16" viewBox="0 0 16 16" fill="none"
            style={{
              transform: showAssumptions ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <path d="M4 6l4 4 4-4" stroke={C.text2} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {showAssumptions && (
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderTop: "none",
            borderRadius: "0 0 12px 12px", padding: "6px 18px 16px",
          }}>
            <p style={{ fontSize: 12.5, color: C.text3, margin: "12px 0 4px", lineHeight: 1.5 }}>
              Wartości uśrednione, oparte na faktycznych rozmowach i obserwacjach z wdrożeń u naszych klientów.
              Możesz dostosować każdy parametr do swojej firmy.
            </p>
            {Object.entries(DEFAULT_ASSUMPTIONS).map(([key, a]) => (
              <AssumptionRow key={key} a={{ ...a, value: A[key] }} onChange={(v) => setAssumption(key, v)} />
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gap: 14, marginBottom: 16 }}>
        <ResultBlock
          title="Nieprecyzyjne rozliczanie czasu pracy"
          total={calc.osA.total}
          accent={C.blue}
          items={[
            { label: "Rozbieżności w czasie pracy", value: calc.osA.absencje },
            { label: "Zaokrąglenia godzin w górę", value: calc.osA.zaokraglenia },
          ]}
        />
        <ResultBlock
          title="Czas tracony na administrację"
          total={calc.osB.total}
          accent={C.blue}
          items={[
            { label: "Zbieranie i przesyłanie papierowych list", value: calc.osB.zbieranie },
            { label: "Przepisywanie godzin i naliczanie wypłat", value: calc.osB.przepisywanie },
            { label: "Spory i wyjaśnienia przy wypłatach", value: calc.osB.spory },
            { label: "Sprawdzanie salda urlopów", value: calc.osB.urlopy },
            { label: "Rozliczanie kosztów inwestycji", value: calc.osB.rentownosc },
          ]}
        />
      </div>

      <div style={{
        background: C.greenLight, border: `1px solid #c5e3d0`, borderRadius: 14,
        padding: "16px 20px", marginBottom: 24, display: "flex", gap: 14, alignItems: "flex-start",
      }}>
        <div style={{ fontSize: 22, lineHeight: 1 }}>💡</div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 6px", color: C.green }}>
            Tego nie daliśmy rady policzyć...
          </p>
          <p style={{ fontSize: 13, color: C.text2, margin: "0 0 8px", lineHeight: 1.5 }}>
            <strong style={{ color: C.text }}>Znasz dokładny koszt każdej inwestycji.</strong> Wiesz, ile
            roboczogodzin pochłonęła konkretna budowa, a więc ile realnie było zysku. Robisz trafniejsze wyceny.
          </p>
          <p style={{ fontSize: 13, color: C.text2, margin: 0, lineHeight: 1.5 }}>
            <strong style={{ color: C.text }}>Notatki i zdjęcia z budowy w jednym miejscu.</strong> Każda
            przypisana do konkretnej inwestycji. Wracasz do nich w każdej chwili, nawet po latach.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 20 }}>
        <a
          href="/register"
          style={btn(true)}
          onClick={(ev) => {
            ev.preventDefault();
            logRegisterClick("savings_calculator");
            register("savings_calculator");
          }}
          onMouseEnter={(ev) => (ev.currentTarget.style.background = C.blueHover)}
          onMouseLeave={(ev) => (ev.currentTarget.style.background = C.blue)}
        >
          Wypróbuj eGodziny za darmo
        </a>
        <a
          href="https://calendly.com/krzysztof-warda-egodziny/30min"
          target="_blank"
          rel="noopener noreferrer"
          style={btn(false)}
        >
          Umów bezpłatne demo
        </a>
      </div>
    </div>
  );
}
